import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService, AuthService, FilesService, SchoolsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { loadMe } from '@store/actions/profile.actions';
import { getMe, getUserLanguages } from '@store/reducers/profile.reducer';
import {
  CSchool,
  CSchoolParticipant,
  ESchoolParticipantRole,
  IContentPane,
  ImageSizeEnum,
  Subject,
  User,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonState, OnUiCover } from 'onclass-ui';
import { of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-school-card',
  templateUrl: './school-card.component.html',
  styleUrls: ['./school-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolCardComponent extends ChangableComponent implements OnInit {
  // DI
  public deviceService = inject(DeviceDetectorService);
  private store = inject(Store);
  private schoolService = inject(SchoolsService);
  private filesService = inject(FilesService);
  private authService = inject(AuthService);
  private accountService = inject(AccountService);
  private router = inject(Router);
  private location = inject(Location);

  @Input() public set school(school: Partial<CSchool>) {
    if (this._school?.id !== school?.id) {
      this._school = school;
      this.loadSchool();
    }
  }

  public get school(): Partial<CSchool> {
    return this._school;
  }

  @Input() view: 'default' | 'mini' = 'default';
  @Input() modal = false;
  @Output() favoriteChanged = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<boolean>();
  @Output() prepared = new EventEmitter<string>();
  public title: string;
  public description: string;
  public coverImg: OnUiCover;
  public participants: CSchoolParticipant[] = [];
  public user: User;
  public subjects: Subject[] = [];
  public schoolUrl: string;
  public fullSchoolUrl: string;
  public addParticipantActionState: OnUiButtonState = 'default';
  public schoolForm = false;
  public alertOpened = false;
  public copied = false;
  public cardInModal: boolean;
  public loaded: boolean;
  public subjectId: number;
  public originalUrl: string;

  private _school: Partial<CSchool>;
  private _me: User;
  private _language: string;

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    this.store
      .select(getUserLanguages)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this._language = res[0];
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get me(): User {
    return this._me;
  }

  public getSubjectTitle(subject: Subject) {
    if (this._language === 'ru') {
      return subject['title_ru'];
    } else {
      return subject['title_en'];
    }
  }

  public participantsTrackByFn(index, participant: CSchoolParticipant): string {
    return participant.account_id;
  }

  public subjectsTrackByFn(index, subject: Subject): number {
    return subject.id;
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }

  public onRemoveSchool() {
    if (!this.meIsOwnerSchool) {
      return;
    }
    this.schoolService
      .removeSchool(this._school.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.onToggleAlert();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onToggleAlert() {
    this.alertOpened = !this.alertOpened;
    this.markForCheck();
  }

  public onEditSchool() {
    this.onToggleForm(true);
  }

  public onAddParticipant() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {
        this.detectChanges();
      });
    } else {
      this.addParticipantActionState = 'progress';
      this.schoolService
        .createParticipant(this._school.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.addParticipantActionState = 'done';
            this.loadSchool();
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.addParticipantActionState = 'fail';
            this.detectChanges();
          },
        });

      this.setTimeout(() => {
        this.addParticipantActionState = 'default';
        this.detectChanges();
      }, 2000);
    }
  }

  public get meIsOwnerSchool(): boolean {
    if (!this._school?.owners) {
      return false;
    }
    return !!this._school?.owners
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this.me?.id);
  }

  public get meIsTeacherSchool(): boolean {
    if (!this._school?.teachers) {
      return false;
    }
    return !!this._school?.teachers
      .filter((_p) => _p.role === ESchoolParticipantRole.teacher)
      .find((_part) => _part.account_id === this.me?.id);
  }

  public get meIsStudentSchool(): boolean {
    if (!this._school?.participants) {
      return false;
    }
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.student)
      .find((_part) => _part.account_id === this.me?.id);
  }

  public filteredTeachers(participants: CSchoolParticipant[]): CSchoolParticipant[] {
    const owners = participants.filter((_part) => _part.role === ESchoolParticipantRole.owner);
    const teachers = participants.filter((_part) => _part.role === ESchoolParticipantRole.teacher);
    return uniqBy(
      [...owners, ...teachers].filter((participant) => !!participant),
      'account_id',
    );
  }

  public filteredStudents(participants: CSchoolParticipant[]): CSchoolParticipant[] {
    if (!participants?.length) {
      return [];
    }
    return participants.filter((_part) => _part.role === ESchoolParticipantRole.student);
  }

  public onToggleForm(e?: boolean) {
    this.schoolForm = !this.schoolForm;
    if (e) {
      this.changed.emit(true);
    }
    this.detectChanges();
  }

  public onCopied(event: any) {
    this.copied = true;
    this.markForCheck();

    this.setTimeout(() => {
      this.copied = false;
      this.markForCheck();
    }, 1000);
  }

  public avatarParticipant(participant: any): OnUiCover {
    return {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(participant?.userpic_id, ImageSizeEnum.sm),
      title: AccountService.getUserFullName(participant),
      form: 'circle',
    };
  }

  public loadParticipant$(id: string) {
    if (!id) {
      return of(null);
    }
    return this.accountService.getUserById(id, 'LOAD_TIME');
  }

  public onOpenCardInModal() {
    const currentUrl = this.location.path(false);
    if (currentUrl !== this.schoolUrl) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(this.schoolUrl);
    }
    this.cardInModal = true;
    this.detectChanges();
  }

  public onCloseCardInModal() {
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
      this.originalUrl = null;
    }

    this.cardInModal = false;
    this.detectChanges();
  }

  public onNavigateToSchool() {
    this.router.navigate([this.schoolUrl]).then();
  }

  public get isParticipant() {
    return this.meIsOwnerSchool || this.meIsTeacherSchool || this.meIsStudentSchool;
  }

  public navigateToPage(): void {
    this.router.navigate([this.schoolUrl]);
  }

  public setSubject(id: number) {
    this.subjectId = id;
    this.onOpenCardInModal();
    this.detectChanges();
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  private loadSchool() {
    const participantIds = [];
    const schoolParticipants = [
      ...(this._school?.owners || []),
      ...(this._school?.teachers || []),
      ...(this._school?.participants || []),
    ];
    schoolParticipants.forEach((participant) => {
      participantIds.push(participant.account_id);
    });
    this.loaded = true;
    this.prepareCard();
    this.detectChanges();
  }

  private prepareCard() {
    this.title = this._school?.title;
    this.description = this._school['description'];
    this.participants = this._school.owners
      ? [...(this._school.owners || []), ...(this._school.teachers || []), ...(this._school.participants || [])]
      : this._school.participants || [];
    this.subjects = this._school?.subjects;
    this.schoolUrl = SchoolsService.schoolRoute(this._school, false).join('/');
    this.fullSchoolUrl = SchoolsService.schoolRoute(this._school).join('/');
    this.coverImg = {
      type: 'cover',
      img: this._school?.__opts?.images.length
        ? this.prepareStorageUrl(this._school?.__opts?.images[0].wide)
        : FilesService.getFileUrlBySize(this._school.head_cover_id, ImageSizeEnum.wide),
      title: this.title,
      link: this.schoolUrl,
      form: this.deviceService.isMobile() ? 'base' : 'none',
    };
    this.prepared.emit(this._school.id);
  }
}
